/**
 * Schema-Datei — provision a data model from an uploaded configuration file,
 * next to the built-in "Schema anlegen / ergänzen" button on the system console.
 *
 * The built-in AssetRadar model is compiled into the backend, which is why the
 * provisioning button takes no input. This card adds the operator's own model:
 * a YAML (or JSON) file that declares classes ("Karten"), their attributes, the
 * relations between them and the lookup lists. Two guard rails make it safe to
 * hand out:
 *
 *   - **Additive.** Provisioning only ever creates what CMDBuild is missing, so
 *     a file can add cards or extend an existing class, never rename or delete
 *     one. The SPA's own forms keep following the built-in model.
 *   - **Preview first.** "Nur prüfen" is checked by default: the server reads
 *     CMDBuild and reports what a real run would create, writing nothing. A
 *     malformed file answers with EVERY validation problem at once, so one
 *     round of fixes is enough.
 *
 * "Vorlage herunterladen" hands out the current model in exactly the upload
 * format — the intended way to start a file, since it round-trips unchanged.
 */

import { Trans, useLingui } from '@lingui/react/macro';
import { useRef, useState, type ReactNode } from 'react';

import { errorMessage, exportSchemaFile, uploadSchemaFile } from '../../lib/api';
import { useToast } from '../../components/Toast';
import type { SchemaUploadResponse } from '../../lib/types';
import OperationCard from './OperationCard';

export interface SchemaFileCardProps {
  /** Non-null disables every control and explains why (native tooltip). */
  disabledReason?: string | null;
  /** Called after a run with the translated summary and the server's error list. */
  onDone?: (message: string, errors: readonly string[]) => void;
}

const ACCEPT = '.yaml,.yml,.json';

export function SchemaFileCard({
  disabledReason = null,
  onDone,
}: SchemaFileCardProps): ReactNode {
  const { t } = useLingui();
  const toast = useToast();
  const inputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [dryRun, setDryRun] = useState(true);
  const [exporting, setExporting] = useState(false);

  const locked = disabledReason !== null;

  /** Hand the current model over as a file the operator can edit and re-upload. */
  async function onExport(): Promise<void> {
    setExporting(true);
    try {
      const response = await exportSchemaFile();
      const url = URL.createObjectURL(new Blob([response.yaml], { type: 'application/yaml' }));
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = response.filename;
      anchor.click();
      URL.revokeObjectURL(url);
      const { classes, relations } = response.counts;
      toast.success(t`Vorlage heruntergeladen: ${classes} Klassen, ${relations} Relationen.`);
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setExporting(false);
    }
  }

  const buttonReason = locked
    ? disabledReason
    : file === null
      ? t`Bitte zuerst eine Schema-Datei (YAML oder JSON) auswählen.`
      : null;

  return (
    <OperationCard<SchemaUploadResponse>
      title={t`Schema-Datei hochladen`}
      description={
        <Trans>
          Eigenes Datenmodell aus einer YAML- oder JSON-Datei anlegen: Klassen (Karten) mit
          Attributen, Relationen und Lookup-Listen. Vorhandenes wird nie umbenannt, geändert oder
          gelöscht. Die Formulare von AssetRadar folgen weiterhin dem eingebauten Modell.
        </Trans>
      }
      buttonLabel={dryRun ? t`Datei prüfen (Vorschau)` : t`Schema provisionieren`}
      runningLabel={dryRun ? t`Prüfe…` : t`Provisioniere…`}
      disabledReason={buttonReason}
      run={() =>
        file === null
          ? Promise.reject(new Error(t`Keine Schema-Datei ausgewählt.`))
          : uploadSchemaFile(file, dryRun)
      }
      errorsOf={(response) => response.result.errors}
      onDone={(response, errors) => {
        const r = response.result;
        const classes = r.created_classes.length;
        const attributes = r.created_attributes.length;
        const domains = r.created_domains.length;
        const lookups = r.created_lookups.length;
        onDone?.(
          response.dry_run
            ? t`Vorschau: ${classes} Klassen, ${attributes} Attribute, ${domains} Domains, ${lookups} Lookups.`
            : t`Provisioniert: ${classes} Klassen, ${attributes} Attribute, ${domains} Domains, ${lookups} Lookups.`,
          errors,
        );
      }}
      summarise={(response) => {
        const r = response.result;
        const { classes, attributes, relations, lookups } = response.counts;
        const newClasses = r.created_classes.length;
        const newAttributes = r.created_attributes.length;
        const newDomains = r.created_domains.length;
        const newLookups = r.created_lookups.length;
        const oldClasses = r.skipped_classes.length;
        const oldAttributes = r.skipped_attributes.length;
        const oldDomains = r.skipped_domains.length;
        const oldLookups = r.skipped_lookups.length;
        return (
          <>
            {response.dry_run ? (
              <strong><Trans>Vorschau, nichts geschrieben.</Trans> </strong>
            ) : null}
            {t`Datei: ${classes} Klassen, ${attributes} Attribute, ${relations} Relationen, ${lookups} Lookups.`}{' '}
            {response.dry_run
              ? t`Würde neu anlegen: ${newClasses} Klassen, ${newAttributes} Attribute, ${newDomains} Domains, ${newLookups} Lookups.`
              : t`Neu angelegt: ${newClasses} Klassen, ${newAttributes} Attribute, ${newDomains} Domains, ${newLookups} Lookups.`}{' '}
            {t`Bereits vorhanden: ${oldClasses} Klassen, ${oldAttributes} Attribute, ${oldDomains} Domains, ${oldLookups} Lookups.`}
            {response.warnings.length > 0 ? (
              <ul className="ops-schemafile-warn">
                {response.warnings.map((warning) => (
                  <li key={warning}>{warning}</li>
                ))}
              </ul>
            ) : null}
          </>
        );
      }}
    >
      <div className="ops-schemafile">
        <div className="ops-schemafile-pick">
          <input
            ref={inputRef}
            type="file"
            accept={ACCEPT}
            hidden
            onChange={(event) => setFile(event.target.files?.[0] ?? null)}
          />
          <button
            type="button"
            className="ghost"
            onClick={() => inputRef.current?.click()}
            disabled={locked}
            title={disabledReason ?? undefined}
          >
            <Trans>Datei wählen…</Trans>
          </button>
          {file !== null ? (
            <span className="imp-file">{file.name}</span>
          ) : (
            <span className="muted"><Trans>YAML oder JSON</Trans></span>
          )}
        </div>

        <label className="check">
          <input
            type="checkbox"
            checked={dryRun}
            onChange={(event) => setDryRun(event.target.checked)}
            disabled={locked}
          />
          <Trans>Nur prüfen (Vorschau, ohne in CMDBuild zu schreiben)</Trans>
        </label>

        <div className="ops-schemafile-tpl">
          <button
            type="button"
            className="ghost"
            onClick={() => void onExport()}
            disabled={locked || exporting}
            title={disabledReason ?? undefined}
          >
            {exporting ? t`Exportiere…` : t`Vorlage herunterladen`}
          </button>
          <span className="muted">
            <Trans>Das aktuelle Modell im Upload-Format, als Ausgangspunkt für die eigene Datei.</Trans>
          </span>
        </div>

        <details className="ops-json">
          <summary><Trans>Dateiformat</Trans></summary>
          {/* Sample file with CMDB names and German comments: file content, deliberately untranslated. */}
          <pre>{`version: 1
classes:                       # oder: cards
  - name: Lieferant            # Klassenname (Buchstaben, Ziffern, _)
    description: Lieferant eines Bauteils
    parent: null               # optional: Elternklasse
    attributes:
      - name: Name
        type: string           # string text integer decimal double boolean
        description: Firmenname #  date dateTime reference lookup
        unique: true           # weitere Schlüssel gehen an CMDBuild
      - name: Status
        type: lookup
        lookup_type: Lieferant - Status
      - name: KomponenteTypeID
        type: reference
        target_class: KomponenteType     # Klasse, auf die verwiesen wird
        domain: LieferantKomponenteType  # Relation, über die verwiesen wird
relations:                     # oder: domains
  - name: LieferantKomponenteType
    source: Lieferant
    destination: KomponenteType
    cardinality: 1:N           # 1:1 1:N N:1 N:N
    label: liefert
    inverse_label: geliefert von
lookups:
  - type: Lieferant - Status
    values:
      - code: AKTIV
        description: Aktiv`}</pre>
        </details>
      </div>
    </OperationCard>
  );
}

export default SchemaFileCard;
